/**
 * useAiSuggestions Hook
 *
 * Custom hook for managing AI suggestions state and operations.
 * Handles fetching, feedback submission, and selection state.
 *
 * @module features/ai-button/hooks/useAiSuggestions
 * @see SPEC-012 Requirements 15.1-15.6
 * @see SPEC-019 Requirements 11.2, 11.3, 11.4, 11.6, 14.6
 */

import { useState, useCallback, useRef } from 'react';
import type {
  SuggestResponse,
  BacklinkSuggestion,
  TagSuggestion,
  CandidateKind,
  FeedbackAction,
  AiApplySelection,
  SuggestHint,
} from '@linkode/shared';
import { aiApiClient } from '../api/aiApiClient';
import { dryRunApply, type DryRunResult } from '../apply/dry-run';

// ============================================================================
// Types
// ============================================================================

/**
 * State of the AI suggestions hook
 */
export interface AiSuggestionsState {
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Current proposal ID */
  proposalId: string | null;
  /** Backlink suggestions */
  backlinks: BacklinkSuggestion[];
  /** Tag suggestions */
  tags: TagSuggestion[];
  /** Whether results are degraded */
  degraded: boolean;
  /** Degradation reason */
  degradeReason?: string;
  /** Selected backlink IDs */
  selectedBacklinks: Set<string>;
  /** Selected tag strings */
  selectedTags: Set<string>;
  /** Feedback given (candidateId -> action) */
  feedback: Map<string, FeedbackAction>;
  /** Hint from API response (SPEC-019) */
  hint?: SuggestHint;
  /** Dry-run preview result (SPEC-019) */
  dryRunResult?: DryRunResult;
  /** Last apply result (SPEC-019) */
  lastApplyResult?: { success: boolean; count: number };
  /** Last apply timestamp (SPEC-019) */
  lastApplyTimestamp?: number;
}

/**
 * Actions returned by the hook
 */
export interface AiSuggestionsActions {
  /** Fetch suggestions for a document */
  fetchSuggestions: (docId: string) => Promise<void>;
  /** Toggle backlink selection */
  toggleBacklink: (docId: string) => void;
  /** Toggle tag selection */
  toggleTag: (tag: string) => void;
  /** Submit feedback for a candidate */
  submitFeedback: (
    docId: string,
    candidateId: string,
    kind: CandidateKind,
    action: FeedbackAction
  ) => Promise<void>;
  /** Apply selected suggestions - returns true on success, false on failure */
  applySelected: (docId: string) => Promise<boolean>;
  /** Select all */
  selectAll: () => void;
  /** Clear all selections */
  clearSelections: () => void;
  /** Reset state */
  reset: () => void;
  /** Get dry-run preview (SPEC-019) */
  getDryRunPreview: (markdown: string) => Promise<DryRunResult>;
  /** Cancel pending fetch (SPEC-019) */
  cancelFetch: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AiSuggestionsState = {
  isLoading: false,
  error: null,
  proposalId: null,
  backlinks: [],
  tags: [],
  degraded: false,
  degradeReason: undefined,
  selectedBacklinks: new Set(),
  selectedTags: new Set(),
  feedback: new Map(),
  hint: undefined,
  dryRunResult: undefined,
  lastApplyResult: undefined,
  lastApplyTimestamp: undefined,
};

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useAiSuggestions Hook
 *
 * @returns State and actions for AI suggestions
 */
export function useAiSuggestions(): [AiSuggestionsState, AiSuggestionsActions] {
  const [state, setState] = useState<AiSuggestionsState>(initialState);

  // AbortController ref for fetch cancellation (SPEC-019 Requirement 14.6)
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track if fetch was cancelled
  const fetchCancelledRef = useRef<boolean>(false);

  const fetchSuggestions = useCallback(async (docId: string) => {
    // Cancel any pending fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController
    abortControllerRef.current = new AbortController();
    fetchCancelledRef.current = false;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response: SuggestResponse = await aiApiClient.getSuggestions(docId);

      // Don't update state if fetch was cancelled
      if (fetchCancelledRef.current) {
        return;
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        proposalId: response.proposalId,
        backlinks: response.backlinks,
        tags: response.tags,
        degraded: response.degraded,
        degradeReason: response.degradeReason,
        hint: response.hint,
        selectedBacklinks: new Set(),
        selectedTags: new Set(),
        feedback: new Map(),
        dryRunResult: undefined,
      }));
    } catch (err) {
      // Don't update state if fetch was cancelled
      if (fetchCancelledRef.current) {
        return;
      }

      const message = err instanceof Error ? err.message : 'Failed to fetch suggestions';
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
    }
  }, []);

  const toggleBacklink = useCallback((docId: string) => {
    setState((prev) => {
      const newSet = new Set(prev.selectedBacklinks);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return { ...prev, selectedBacklinks: newSet };
    });
  }, []);

  const toggleTag = useCallback((tag: string) => {
    setState((prev) => {
      const newSet = new Set(prev.selectedTags);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return { ...prev, selectedTags: newSet };
    });
  }, []);

  const submitFeedback = useCallback(
    async (docId: string, candidateId: string, kind: CandidateKind, action: FeedbackAction) => {
      if (!state.proposalId) return;

      try {
        await aiApiClient.submitFeedback({
          docId,
          proposalId: state.proposalId,
          candidateId,
          kind,
          action,
        });

        setState((prev) => {
          const newFeedback = new Map(prev.feedback);
          newFeedback.set(candidateId, action);
          return { ...prev, feedback: newFeedback };
        });
      } catch (err) {
        console.error('Failed to submit feedback:', err);
      }
    },
    [state.proposalId]
  );

  const applySelected = useCallback(
    async (docId: string): Promise<boolean> => {
      if (!state.proposalId) return false;

      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        const accepted: AiApplySelection = {
          backlinks: Array.from(state.selectedBacklinks),
          tags: Array.from(state.selectedTags),
        };

        const rejected: AiApplySelection = {
          backlinks: state.backlinks
            .filter((b) => !state.selectedBacklinks.has(b.docId))
            .map((b) => b.docId),
          tags: state.tags.filter((t) => !state.selectedTags.has(t.tag)).map((t) => t.tag),
        };

        await aiApiClient.applySuggestions({
          docId,
          proposalId: state.proposalId,
          accepted,
          rejected,
        });

        setState(initialState);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to apply suggestions';
        setState((prev) => ({ ...prev, isLoading: false, error: message }));
        return false;
      }
    },
    [state.proposalId, state.selectedBacklinks, state.selectedTags, state.backlinks, state.tags]
  );

  const selectAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedBacklinks: new Set(prev.backlinks.map((b) => b.docId)),
      selectedTags: new Set(prev.tags.map((t) => t.tag)),
    }));
  }, []);

  const clearSelections = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedBacklinks: new Set(),
      selectedTags: new Set(),
    }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  /**
   * Cancel pending fetch request (SPEC-019 Requirement 14.6)
   */
  const cancelFetch = useCallback(() => {
    fetchCancelledRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState((prev) => ({ ...prev, isLoading: false }));
  }, []);

  /**
   * Get dry-run preview of applying selected suggestions (SPEC-019)
   */
  const getDryRunPreview = useCallback(
    async (markdown: string): Promise<DryRunResult> => {
      const now = Date.now();
      const writeback = {
        ai_tags: Array.from(state.selectedTags),
        ai_links: Array.from(state.selectedBacklinks).map((docId) => {
          const backlink = state.backlinks.find((b) => b.docId === docId);
          return {
            id: docId,
            title: backlink?.title ?? docId,
            reason: backlink?.reason ?? '',
            source: 'ai' as const,
            appliedAtMs: now,
          };
        }),
      };

      const result = dryRunApply(markdown, writeback);

      setState((prev) => ({ ...prev, dryRunResult: result }));

      return result;
    },
    [state.selectedTags, state.selectedBacklinks, state.backlinks]
  );

  const actions: AiSuggestionsActions = {
    fetchSuggestions,
    toggleBacklink,
    toggleTag,
    submitFeedback,
    applySelected,
    selectAll,
    clearSelections,
    reset,
    getDryRunPreview,
    cancelFetch,
  };

  return [state, actions];
}
